import api from './api';

export interface CasinoInfo {
  id: string;
  name: string;
  type: string;
  location: string;
  ownerId: string | null;
  ownerName: string | null;
  maxBet: number;
  currentBalance: number;
  isAvailable: boolean;
  takeOverPrice: number;
}

export interface BlackjackGame {
  gameId: string;
  playerHand: number[];
  dealerHand: number[];
  playerTotal: number;
  dealerTotal: number;
  betAmount: number;
  gameState: 'playing' | 'player-turn' | 'dealer-turn' | 'complete';
  canHit: boolean;
  canStand: boolean;
  canDouble: boolean;
  isDoubled: boolean;
}

export interface RouletteBet {
  type: 'number' | 'color' | 'odd' | 'even' | 'column' | 'dozen' | 'low' | 'high';
  value: number | string;
  amount: number;
  payout: number;
}

export interface RouletteGame {
  gameId: string;
  bets: RouletteBet[];
  totalBet: number;
  gameState: 'betting' | 'spinning' | 'complete';
  winningNumber: number | null;
  winningColor: string | null;
  results: {
    bet: RouletteBet;
    won: boolean;
    payout: number;
  }[];
}

export interface BlackjackResult {
  success: boolean;
  message: string;
  playerHand: number[];
  dealerHand: number[];
  playerTotal: number;
  dealerTotal: number;
  gameResult: 'win' | 'lose' | 'push' | 'blackjack';
  betAmount: number;
  winnings: number;
  casinoBalance: number;
}

export const casinoService = {
  // Get casinos by location
  async getCasinosByLocation(location: string) {
    const response = await api.get(`/casino/location/${location}`);
    return response.data;
  },

  // Get user's owned casinos
  async getMyCasinos() {
    const response = await api.get('/casino/my-casinos');
    return response.data;
  },

  // Get available casinos for take over
  async getAvailableCasinos() {
    const response = await api.get('/casino/available');
    return response.data;
  },

  // Set maximum bet (casino owner only)
  async setMaxBet(casinoId: string, maxBet: number) {
    const response = await api.post('/casino/set-max-bet', { casinoId, maxBet });
    return response.data;
  },

  // Take over an available casino
  async takeOverCasino(casinoId: string) {
    const response = await api.post('/casino/take-over', { casinoId });
    return response.data;
  },

  // Blackjack game methods
  async startBlackjackGame(casinoId: string, betAmount: number) {
    const response = await api.post('/casino/blackjack/start', { casinoId, betAmount });
    return response.data;
  },

  async hit(gameId: string, casinoId: string) {
    const response = await api.post('/casino/blackjack/hit', { gameId, casinoId });
    return response.data;
  },

  async stand(gameId: string, casinoId: string) {
    const response = await api.post('/casino/blackjack/stand', { gameId, casinoId });
    return response.data;
  },

  async double(gameId: string, casinoId: string) {
    const response = await api.post('/casino/blackjack/double', { gameId, casinoId });
    return response.data;
  },

  async getGameState(gameId: string) {
    const response = await api.get(`/casino/blackjack/game/${gameId}`);
    return response.data;
  },

  // Roulette game methods
  async startRouletteGame(casinoId: string) {
    const response = await api.post('/casino/roulette/start', { casinoId });
    return response.data;
  },

  async placeRouletteBet(gameId: string, casinoId: string, bet: RouletteBet) {
    const response = await api.post('/casino/roulette/bet', { gameId, casinoId, bet });
    return response.data;
  },

  async spinRoulette(gameId: string, casinoId: string) {
    const response = await api.post('/casino/roulette/spin', { gameId, casinoId });
    return response.data;
  },

  async getRouletteGameState(gameId: string) {
    const response = await api.get(`/casino/roulette/game/${gameId}`);
    return response.data;
  },

  async getRouletteHistory() {
    const response = await api.get('/casino/roulette/history');
    return response.data;
  },

  async reactivateCasino(casinoId: string) {
    const response = await api.post('/casino/reactivate', { casinoId });
    return response.data;
  },

  async getRoulettePayouts() {
    const response = await api.get('/casino/roulette/payouts');
    return response.data;
  }
};
